/**
 * SalesOrderListPage (S2.2) — rebuilt on the house list stack.
 *
 * useSalesOrdersTable (useAntdTable + pre-first-fetch guard) + soStore filters
 * + useListPage state triad + GenericFilterPopover (status / price status /
 * customer / order-date recency). Columns: SO # (bold, highlighted, external
 * order number secondary — the import dedupe key) / Customer / Status /
 * 價格審核 hold (shown only when pending — affordance, not status noise) /
 * Order date / Total (+ 預估毛利 secondary) / 80px fixed action column.
 * Whole-row nav to the detail page; NetSuite/Excel intake moves to the S2.4
 * import drawer (not on this page).
 */

import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, App, Badge, Button, Dropdown, Input, Table, Tag, theme } from 'antd';
import type { TableProps } from 'antd';
import {
  EyeOutlined,
  FilterOutlined,
  MoreOutlined,
  PlusOutlined,
  SearchOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import GenericFilterPopover from '@/components/common/GenericFilterPopover';
import type { FieldConfig, FilterValues } from '@/components/common/GenericFilterPopover';
import { HighlightText } from '@/components/common/HighlightText';
import { useListPage } from '@/hooks/useListPage';
import type { TableHookResult } from '@/hooks/useListPage';
import {
  extractApiError,
  useSalesOrderMutations,
  useSalesOrdersTable,
} from '@/hooks/useSalesOrders';
import { useCustomerOptions } from '@/hooks/useCustomers';
import { useProductOptions } from '@/hooks/useProducts';
import { useSoStore } from '@/stores/soStore';
import type {
  SoDateRangeFilter,
  SoFilters,
  SoPriceStatusFilter,
  SoStatusFilter,
} from '@/stores/soStore';
import { useFeature } from '@/features/flags';
import type { SalesOrder, SalesOrderStatus } from '@/types/api/salesOrder';
import { SO_PRICE_DEVIATION_THRESHOLD_PCT } from '@/types/api/salesOrder';
import { SALES_ORDER_STATUS_ORDER } from '@/types/api/salesOrder';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import { formatAmount, formatMoney, formatPct, holdReasonKey } from './components/soFormat';
import { DismissibleTooltip } from './components/DismissibleTooltip';

/** Popover-local filter value shape for the SO list. */
interface SoFilterPopoverValues extends FilterValues {
  status?: string;
  priceStatus?: string;
  customer?: (string | number)[];
  dateRange?: string;
}

/** antd Tag preset per status (StatusTag precedent; no purple literal). */
const STATUS_TAG_COLOR: Record<SalesOrderStatus, string> = {
  draft: 'default',
  confirmed: 'blue',
  converted_to_po: 'geekblue',
  shipped: 'cyan',
  invoiced: 'gold',
  paid: 'green',
};

const DATE_RANGE_VALUES: SoDateRangeFilter[] = ['all', '7d', '30d', '90d'];

/**
 * `?openOnly=1` (SR2.2) narrows the product-scoped view to sales orders that
 * still carry outstanding fulfillment demand — draft / confirmed / awaiting-PO.
 * Shipped-and-later statuses are excluded (their demand is fulfilled or
 * closing). It is a modifier of the `?productId=` deep link (the single scoped
 * intent "open SOs for this product"), never a hidden standalone filter.
 */
const OPEN_SO_STATUSES: ReadonlySet<SalesOrderStatus> = new Set([
  'draft',
  'confirmed',
  'converted_to_po',
]);

const SalesOrderListPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['orders', 'common']);
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const poFromSo = useFeature('procurement.poFromSo');

  // Route-derived `?productId=` (+ `?openOnly=1` modifier) scope (SR2.2). The
  // param carries the canonical internalProductId the 產品管控中心 deep-links
  // with; it is read from the URL (never the SO store), so the list lands
  // already scoped and the chip clears by dropping the params. `openOnly` only
  // takes effect alongside a product scope — never a hidden standalone filter.
  const [searchParams, setSearchParams] = useSearchParams();
  const routeProductId = searchParams.get('productId');
  const routeFilterActive = Boolean(routeProductId);
  const scopedOpenOnly = searchParams.get('openOnly') === '1' && routeFilterActive;

  const {
    filters,
    setSearch,
    setStatus,
    setPriceStatus,
    setCustomerIds,
    setDateRange,
    resetFilters,
  } = useSoStore();

  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);

  const { customers } = useCustomerOptions();
  const { products } = useProductOptions();
  const { createPoFromSo } = useSalesOrderMutations();

  const clearProductFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('productId');
    next.delete('openOnly');
    setSearchParams(next, { replace: true });
  };

  // Badge parity: single-choice fields count when off 'all'; customer counts
  // one per selected customer.
  const customFilterCounters = useMemo(
    () => ({
      status: (value: unknown) => (value === 'all' ? 0 : 1),
      priceStatus: (value: unknown) => (value === 'all' ? 0 : 1),
      customerIds: (value: unknown) => (Array.isArray(value) ? value.length : 0),
      dateRange: (value: unknown) => (value === 'all' ? 0 : 1),
    }),
    []
  );

  const { tableProps, isLoading, refresh, activeFilterCount, isFiltered, listState } = useListPage<
    SoFilters,
    SalesOrder
  >({
    useTableHook: useSalesOrdersTable as (filters: SoFilters) => TableHookResult<SalesOrder>,
    filters,
    filterCountExclusions: ['search'],
    customFilterCounters,
  });

  // Resolve the scoped product (canonical internalProductId, with a raw db-id
  // fallback) for the chip label + the SO-line match key. SO lines carry the
  // DB `productId`, so a route internalProductId is mapped through the product
  // options; if options are cold, the raw param still matches a db-id link.
  const scopedProduct = useMemo(
    () =>
      routeProductId
        ? products.find((p) => p.internalProductId === routeProductId || p.id === routeProductId) ??
          null
        : null,
    [routeProductId, products]
  );
  const scopedProductDbId = scopedProduct?.id ?? routeProductId;

  // Route-scoped view is a client-side narrowing over the fetched page (mock
  // scale returns the full list). The real server-side productId/openOnly
  // filter belongs in useSalesOrders + soStore (out of this slice's file set).
  const displayedRows = useMemo(() => {
    const rows = (tableProps.dataSource ?? []) as SalesOrder[];
    if (!routeFilterActive) return rows;
    return rows.filter((so) => {
      if (!so.lines.some((line) => line.productId === scopedProductDbId)) return false;
      if (scopedOpenOnly && !OPEN_SO_STATUSES.has(so.status)) return false;
      return true;
    });
  }, [tableProps.dataSource, routeFilterActive, scopedProductDbId, scopedOpenOnly]);

  const anyFilterActive = isFiltered || routeFilterActive;

  const clearAllFilters = () => {
    resetFilters();
    clearProductFilter();
  };

  const filterFields: FieldConfig[] = useMemo(
    () => [
      {
        key: 'status',
        label: t('orders:fields.status'),
        type: 'radio-cards',
        defaultValue: 'all',
        options: [
          { value: 'all', label: t('orders:so.filters.allStatuses') },
          ...SALES_ORDER_STATUS_ORDER.map((status) => ({
            value: status,
            label: t(`orders:so.status.${status}`),
          })),
        ],
        resetText: t('common:actions.reset'),
      },
      {
        key: 'priceStatus',
        label: t('orders:so.fields.priceStatus'),
        type: 'radio-cards',
        defaultValue: 'all',
        options: [
          { value: 'all', label: t('orders:so.filters.allPriceStatuses') },
          { value: 'ok', label: t('orders:so.priceStatus.ok') },
          { value: 'pending_approval', label: t('orders:so.priceStatus.pending_approval') },
        ],
        resetText: t('common:actions.reset'),
      },
      {
        key: 'customer',
        label: t('orders:fields.customer'),
        type: 'multiselect',
        options: customers.map((customer) => ({
          value: customer.customerId,
          label: `${customer.customerCode} — ${customer.customerName}`,
        })),
        placeholder: t('orders:so.filters.selectCustomer'),
        resetText: t('common:actions.reset'),
      },
      {
        key: 'dateRange',
        label: t('orders:so.filters.orderDate'),
        type: 'radio-cards',
        defaultValue: 'all',
        options: DATE_RANGE_VALUES.map((value) => ({
          value,
          label: t(`orders:so.filters.dateRange.${value}`),
        })),
        resetText: t('common:actions.reset'),
      },
    ],
    [t, customers]
  );

  const initialPopoverFilters: SoFilterPopoverValues = useMemo(
    () => ({
      status: filters.status,
      priceStatus: filters.priceStatus,
      customer: filters.customerIds,
      dateRange: filters.dateRange,
    }),
    [filters.status, filters.priceStatus, filters.customerIds, filters.dateRange]
  );

  const handleApplyFilters = (values: SoFilterPopoverValues) => {
    setStatus((values.status as SoStatusFilter) || 'all');
    setPriceStatus((values.priceStatus as SoPriceStatusFilter) || 'all');
    setCustomerIds((values.customer || []).map(Number));
    setDateRange(
      DATE_RANGE_VALUES.includes(values.dateRange as SoDateRangeFilter)
        ? (values.dateRange as SoDateRangeFilter)
        : 'all'
    );
  };

  const handleCreatePo = async (so: SalesOrder) => {
    try {
      const res = await createPoFromSo(so.id);
      message.success(t('orders:so.messages.poCreated', { count: res.createdCount }));
      const first = res.data[0];
      if (first) navigate(`/auth/purchase-orders/${first.id}`);
      else refresh();
    } catch (error) {
      const { message: detail } = extractApiError(error);
      message.error(detail || t('common:messages.createFailed'));
    }
  };

  const columns: TableProps<SalesOrder>['columns'] = [
    {
      title: t('orders:fields.soNumber'),
      dataIndex: 'soNumber',
      key: 'soNumber',
      width: 200,
      render: (_: string, record) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span style={{ fontWeight: token.fontWeightStrong }}>
            <HighlightText text={record.soNumber} searchTerm={filters.search} fallback="-" />
          </span>
          {record.externalOrderNumber && (
            <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
              <HighlightText
                text={record.externalOrderNumber}
                searchTerm={filters.search}
                fallback=""
              />
            </span>
          )}
        </div>
      ),
    },
    {
      title: t('orders:fields.customer'),
      dataIndex: 'customerName',
      key: 'customerName',
      width: 260,
      render: (value: string) => (
        <HighlightText text={value} searchTerm={filters.search} fallback="-" />
      ),
    },
    {
      title: t('orders:fields.status'),
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status: SalesOrderStatus) => (
        <Tag color={STATUS_TAG_COLOR[status]} style={{ margin: 0 }}>
          {t(`orders:so.status.${status}`)}
        </Tag>
      ),
    },
    {
      title: t('orders:so.fields.priceStatus'),
      dataIndex: 'priceStatus',
      key: 'priceStatus',
      width: 130,
      render: (priceStatus: SalesOrder['priceStatus'], record) =>
        priceStatus === 'pending_approval' ? (
          <DismissibleTooltip
            title={
              <div className="tw-flex tw-flex-col tw-gap-1">
                {record.priceHoldReasons.length > 0 && (
                  <ul style={{ margin: 0, paddingInlineStart: token.paddingLG }}>
                    {record.priceHoldReasons.map((reason) => (
                      <li key={reason}>
                        {t(holdReasonKey(reason, true), {
                          pct: SO_PRICE_DEVIATION_THRESHOLD_PCT,
                          floor: formatPct(record.marginFloorPct),
                        })}
                      </li>
                    ))}
                  </ul>
                )}
                <span>{t('orders:so.priceStatus.pendingTooltip')}</span>
              </div>
            }
          >
            <Tag color="orange" style={{ margin: 0 }} tabIndex={0}>
              {t('orders:so.priceStatus.pending_approval')}
            </Tag>
          </DismissibleTooltip>
        ) : (
          renderDash()
        ),
    },
    {
      title: t('orders:so.fields.orderDate'),
      dataIndex: 'orderDate',
      key: 'orderDate',
      width: 120,
      render: (value: string) => value || renderDash(),
    },
    {
      title: t('orders:fields.total'),
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 180,
      align: 'right',
      render: (_: number, record) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5 tw-items-end">
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatMoney(record.currency, record.totalAmount)}
          </span>
          {record.totalEstMargin !== null && (
            <span
              style={{
                fontSize: token.fontSizeSM,
                fontVariantNumeric: 'tabular-nums',
                color: record.totalEstMargin >= 0 ? token.colorSuccess : token.colorError,
              }}
            >
              {t('orders:so.lines.margin')} {formatAmount(record.totalEstMargin)}
            </span>
          )}
        </div>
      ),
    },
    {
      title: t('orders:so.lines.action'),
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_: unknown, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'view',
                label: t('common:actions.view'),
                icon: <EyeOutlined />,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  navigate(`/auth/sales-orders/${record.id}`);
                },
              },
              // pending_approval blocks fulfillment (hide, don't disable)
              ...(poFromSo && record.status === 'confirmed' && record.priceStatus === 'ok'
                ? [
                    {
                      key: 'create-po',
                      label: t('orders:createPo'),
                      icon: <ShoppingCartOutlined />,
                      onClick: (e: { domEvent: { stopPropagation: () => void } }) => {
                        e.domEvent.stopPropagation();
                        void handleCreatePo(record);
                      },
                    },
                  ]
                : []),
            ],
          }}
          trigger={['click']}
        >
          <Button
            type="text"
            aria-label={t('orders:so.actionsFor', { soNumber: record.soNumber })}
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="tw-px-6 tw-pb-8">
      <div className="tw-mb-6">
        <h1 style={{ fontSize: token.fontSizeHeading2, fontWeight: token.fontWeightStrong }}>
          {t('orders:so.list.title')}
        </h1>
      </div>

      {/* Search + filter + create toolbar */}
      <div className="tw-mb-6 tw-flex tw-gap-3 tw-items-center">
        <Input
          placeholder={t('orders:so.searchPlaceholder')}
          value={filters.search}
          onChange={(e) => setSearch(e.target.value)}
          variant="outlined"
          size="large"
          suffix={<SearchOutlined style={{ fontSize: token.sizeMD, color: token.colorPrimary }} />}
          style={{
            width: '450px',
            borderRadius: radiusTokens.card,
            height: `${controlHeightTokens.controlHeightXL}px`,
          }}
        />
        <GenericFilterPopover<SoFilterPopoverValues>
          open={filterPopoverOpen}
          onOpenChange={setFilterPopoverOpen}
          onApply={handleApplyFilters}
          initialFilters={initialPopoverFilters}
          fields={filterFields}
          applyText={t('common:actions.apply')}
          cancelText={t('common:actions.cancel')}
        >
          <Badge count={activeFilterCount}>
            <Button
              aria-label={t('common:actions.filter')}
              icon={<FilterOutlined style={{ color: token.colorPrimary }} />}
              size="large"
              style={{
                borderRadius: radiusTokens.card,
                height: `${controlHeightTokens.controlHeightXL}px`,
                width: `${controlHeightTokens.controlHeightXL}px`,
              }}
            />
          </Badge>
        </GenericFilterPopover>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          size="large"
          onClick={() => navigate('/auth/sales-orders/add')}
          style={{
            marginLeft: 'auto',
            borderRadius: radiusTokens.card,
            height: `${controlHeightTokens.controlHeightXL}px`,
            paddingLeft: token.paddingLG,
            paddingRight: token.paddingLG,
          }}
        >
          {t('orders:addSo')}
        </Button>
      </div>

      {/* Route-derived product scope chip (SR2.2) — visible + clearable. The
          scope narrows client-side over the fetched page, so it self-declares
          that cross-page matches aren't resolved yet (data honesty; the
          server-side productId filter is deferred to useSalesOrders/soStore). */}
      {routeFilterActive && (
        <div className="tw-mb-6 tw-flex tw-flex-col tw-gap-2">
          <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
            <Tag
              closable
              onClose={clearProductFilter}
              style={{ margin: 0, paddingBlock: token.paddingXXS }}
            >
              {t('orders:fields.product')}: {scopedProduct?.sku ?? routeProductId}
            </Tag>
          </div>
          <Alert
            type="info"
            showIcon
            message={t('orders:so.states.scopedPageNotice')}
            style={{ borderRadius: radiusTokens.card }}
          />
        </div>
      )}

      {/* Table — S0.3 state triad via TableContainer + ListEmptyState */}
      <TableContainer
        errorState={{
          active: listState === 'error',
          title: t('orders:so.states.loadFailed'),
          retryLabel: t('common:errors.boundary.retry'),
          onRetry: refresh,
        }}
      >
        <Table<SalesOrder>
          {...tableProps}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          dataSource={displayedRows}
          onChange={routeFilterActive ? undefined : tableProps.onChange}
          onRow={(record) => ({
            onClick: () => navigate(`/auth/sales-orders/${record.id}`),
            style: { cursor: 'pointer' },
          })}
          locale={{
            emptyText: anyFilterActive ? (
              <ListEmptyState
                title={t('orders:so.states.noResults')}
                actionLabel={t('orders:so.states.clearFilters')}
                onAction={clearAllFilters}
                actionType="default"
              />
            ) : (
              <ListEmptyState
                title={t('orders:so.states.noOrders')}
                actionLabel={t('orders:so.states.createFirst')}
                onAction={() => navigate('/auth/sales-orders/add')}
                actionIcon={<PlusOutlined />}
              />
            ),
          }}
          pagination={
            routeFilterActive
              ? { pageSize: 10, showSizeChanger: false }
              : { ...tableProps.pagination, showSizeChanger: false }
          }
          scroll={{ x: 1100 }}
        />
      </TableContainer>
    </div>
  );
};

export default SalesOrderListPage;
